/**
 * NvrHealthPanel
 *
 * Displays NVR storage, uptime, firmware version, and retention settings
 * for a specific NVR identified by nvrId prop.
 */

import React, { useEffect } from 'react';
import { useUnifiProtectStore, useNvrHealth } from '../unifi-protect-store';

interface NvrHealthPanelProps {
  nvrId: string;
}

function formatUptime(seconds: number): string {
  if (seconds <= 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

export function NvrHealthPanel({ nvrId }: NvrHealthPanelProps) {
  const nvrHealth = useNvrHealth(nvrId);
  const isConnected = useUnifiProtectStore(s => s.nvrs[nvrId]?.isConnected ?? false);
  const fetchNvrHealth = useUnifiProtectStore(s => s.fetchNvrHealth);

  useEffect(() => {
    if (isConnected) {
      fetchNvrHealth(nvrId);
    }
  }, [isConnected, nvrId, fetchNvrHealth]);

  if (!nvrHealth) return null;

  const storagePct = nvrHealth.storageTotal > 0
    ? Math.round((nvrHealth.storageUsed / nvrHealth.storageTotal) * 100)
    : 0;

  const storageBarColor = storagePct > 85 ? '#ef4444' : storagePct > 65 ? '#f59e0b' : '#22c55e';

  return (
    <div style={{
      background: 'hsl(var(--card))',
      border: '1px solid hsl(var(--border))',
      borderRadius: 12,
      padding: '16px 18px',
    }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: 'hsl(var(--foreground))' }}>
        NVR Health
      </h3>

      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>Storage</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: storageBarColor }}>
            {storagePct}% used
          </span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: 'hsl(var(--border))', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${storagePct}%`,
            background: storageBarColor,
            borderRadius: 3,
            transition: 'width 0.4s ease',
          }} />
        </div>
        <div style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', marginTop: 4 }}>
          {formatBytes(nvrHealth.storageUsed)} used of {formatBytes(nvrHealth.storageTotal)}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
        <Stat label="Uptime" value={formatUptime(nvrHealth.uptime)} />
        <Stat label="Firmware" value={nvrHealth.version} />
        <Stat label="Retention" value={nvrHealth.retentionDays > 0 ? `${nvrHealth.retentionDays}d` : '—'} />
        <Stat label="Cameras" value={String(nvrHealth.cameraCount)} />
        {nvrHealth.hardwarePlatform && (
          <Stat label="Hardware" value={nvrHealth.hardwarePlatform} />
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'hsl(var(--foreground))' }}>{value}</div>
    </div>
  );
}
